import { useEffect, useId, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import AuthLocaleToggle from '@/components/AuthLocaleToggle'
import { useLocaleSync, useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { createRegisterSchema, ROLE_VALUES, type RegisterFormValues } from './schema'
import { registerUser, ApiError } from '@/services/auth'
import { hasAccessToken } from '@/services/session'
import LogoRP from '@/components/LogoRP'
import LogoBrSE from '@/components/LogoBrSE'
import SiteFooter from '@/components/SiteFooter'
import { SunIcon, MoonIcon } from '@/components/Icons'

export default function RegisterPage() {
  const { t } = useUiStrings()
  useLocaleSync()
  const emailId = useId()
  const nameId = useId()
  const passwordId = useId()
  const confirmPasswordId = useId()
  const roleId = useId()

  const navigate = useNavigate()
  const setSession = useStore((s) => s.setSession)
  const theme = useStore((s) => s.theme)
  const toggleTheme = useStore((s) => s.toggleTheme)

  const [success, setSuccess] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const registerSchema = useMemo(
    () =>
      createRegisterSchema({
        emailRequired: t.emailRequired,
        emailInvalid: t.emailInvalid,
        nameRequired: t.nameRequired,
        nameMinLength: t.nameMinLength,
        passwordRequired: t.passwordRequired,
        passwordMinLength: t.passwordMinLength,
        passwordComplexity: t.passwordComplexity,
        confirmPasswordRequired: t.confirmPasswordRequired,
        roleRequired: t.roleRequired,
        passwordsMustMatch: t.passwordsMustMatch,
      }),
    [
      t.confirmPasswordRequired,
      t.emailInvalid,
      t.emailRequired,
      t.nameMinLength,
      t.nameRequired,
      t.passwordComplexity,
      t.passwordMinLength,
      t.passwordRequired,
      t.passwordsMustMatch,
      t.roleRequired,
    ]
  )

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      name: '',
      password: '',
      confirmPassword: '',
      role: 'dev',
    },
  })

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  if (hasAccessToken()) return <Navigate to="/home" replace />

  const onSubmit = async (data: RegisterFormValues) => {
    clearErrors('root')
    try {
      const res = await registerUser(data)
      setSession(
        {
          displayName: res.user.name,
          email: res.user.email,
          plan: res.user.role,
          role: res.user.role,
          authProvider: res.user.authProvider ?? 'local',
        },
        res.accessToken
      )
      setSuccess(t.registrationSuccess)
      setTimeout(() => navigate('/home', { replace: true }), 600)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError('email', { message: t.emailAlreadyRegistered })
          return
        }
        if (err.status === 400 && Object.keys(err.fieldErrors).length > 0) {
          for (const [field, msg] of Object.entries(err.fieldErrors)) {
            setError(field as keyof RegisterFormValues, { message: msg })
          }
          return
        }
        setError('root', { message: err.message })
      } else {
        setError('root', { message: t.unexpectedError })
      }
    }
  }

  const inputClass = (field: keyof RegisterFormValues) =>
    errors[field]
      ? 'input-field border-[var(--error)] focus:border-[var(--error)] focus:ring-[var(--error)]/25'
      : 'input-field'

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <div className="flex flex-1 flex-col items-center justify-center p-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:p-4">
        <div className="w-full max-w-md animate-fade-in rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-[var(--shadow-lg)] sm:p-6 md:p-8">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
            {/* `flex-auto` (not `flex-1`): its basis must reflect the logos'
                actual content width so the row wraps once there's no room for
                both logos plus the locale/theme controls, instead of every
                item shrinking to fit (which clips/squishes the logos — see
                shrink-0 in LogoBrSE.tsx/LogoRP.tsx). `flex-wrap` stays as a
                safety net for narrow mobile widths, but at `sm:` and up the
                logos drop to `h-7` (not grow to `h-10`) specifically so both
                logos + the locale/theme controls fit on one line within the
                card's fixed ~448px width — that combination never fit at
                h-10, wrap or not (both logos + controls need ~448px, the
                card's content area is ~382px). */}
            <div className="flex flex-auto items-center overflow-visible pr-2">
              <LogoBrSE className="h-9 max-h-10 w-auto max-w-full sm:h-7" />
              <LogoRP className="h-9 max-h-10 w-auto max-w-full sm:h-7" />
            </div>
            <div className="flex items-center gap-2">
              <AuthLocaleToggle />
              <button
                type="button"
                onClick={toggleTheme}
                className="rounded-lg p-2 transition-colors hover:bg-[var(--bg-tertiary)]"
                title={theme === 'dark' ? t.lightMode : t.darkMode}
              >
                {theme === 'dark' ? (
                  <SunIcon className="h-[18px] w-[18px] text-[var(--text-secondary)]" />
                ) : (
                  <MoonIcon className="h-[18px] w-[18px] text-[var(--text-secondary)]" />
                )}
              </button>
            </div>
          </div>

          <h1 className="mb-1 text-lg font-bold text-[var(--text-primary)]">{t.registerTitle}</h1>
          <p className="mb-6 text-sm text-[var(--text-secondary)]">{t.registerSubtitle}</p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div>
              <label
                htmlFor={emailId}
                className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
              >
                {t.email} <span className="text-[var(--error)]">*</span>
              </label>
              <input
                id={emailId}
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className={inputClass('email')}
                aria-invalid={errors.email ? 'true' : 'false'}
                {...register('email', { onChange: () => clearErrors('root') })}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-[var(--error)]">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label
                htmlFor={nameId}
                className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
              >
                {t.name} <span className="text-[var(--error)]">*</span>
              </label>
              <input
                id={nameId}
                type="text"
                autoComplete="name"
                placeholder={t.namePlaceholder}
                className={inputClass('name')}
                aria-invalid={errors.name ? 'true' : 'false'}
                {...register('name', { onChange: () => clearErrors('root') })}
              />
              {errors.name && (
                <p className="mt-1.5 text-xs text-[var(--error)]">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label
                htmlFor={passwordId}
                className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
              >
                {t.password} <span className="text-[var(--error)]">*</span>
              </label>
              <div className="relative">
                <input
                  id={passwordId}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={t.passwordPlaceholder}
                  className={`${inputClass('password')} pr-10`}
                  aria-invalid={errors.password ? 'true' : 'false'}
                  {...register('password', { onChange: () => clearErrors('root') })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
                  aria-label={showPassword ? t.hidePassword : t.showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeIcon className="h-4 w-4" />
                  ) : (
                    <EyeOffIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password ? (
                <p className="mt-1.5 text-xs text-[var(--error)]">{errors.password.message}</p>
              ) : (
                <p className="mt-1.5 text-xs text-[var(--text-secondary)]">
                  {t.passwordRequirements}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor={confirmPasswordId}
                className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
              >
                {t.confirmPassword} <span className="text-[var(--error)]">*</span>
              </label>
              <div className="relative">
                <input
                  id={confirmPasswordId}
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={t.confirmPasswordPlaceholder}
                  className={`${inputClass('confirmPassword')} pr-10`}
                  aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                  {...register('confirmPassword', { onChange: () => clearErrors('root') })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
                  aria-label={showConfirmPassword ? t.hidePassword : t.showPassword}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeIcon className="h-4 w-4" />
                  ) : (
                    <EyeOffIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1.5 text-xs text-[var(--error)]">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor={roleId}
                className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]"
              >
                {t.role} <span className="text-[var(--error)]">*</span>
              </label>
              <select
                id={roleId}
                className={`${inputClass('role')} appearance-none bg-no-repeat pr-10`}
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
                  backgroundPosition: 'right 0.875rem center',
                  backgroundSize: '16px 16px',
                }}
                aria-invalid={errors.role ? 'true' : 'false'}
                {...register('role', { onChange: () => clearErrors('root') })}
              >
                {ROLE_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {value === 'dev'
                      ? t.roleDeveloper
                      : value === 'pm'
                        ? t.roleProjectManager
                        : t.roleBrse}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1.5 text-xs text-[var(--error)]">{errors.role.message}</p>
              )}
            </div>

            {errors.root && (
              <p className="text-sm text-[var(--error)]" role="alert">
                {errors.root.message}
              </p>
            )}
            {success && (
              <p className="text-sm text-[var(--success,#16a34a)]" role="status">
                {success}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary w-full disabled:pointer-events-none disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? t.registering : t.createAccount}
            </button>

            <p className="text-center text-xs text-[var(--text-secondary)]">
              {t.haveAccount}{' '}
              <Link
                to="/login"
                className="font-semibold text-[var(--text-primary)] hover:underline"
              >
                {t.login}
              </Link>
            </p>
          </form>
        </div>
      </div>
      <SiteFooter className="bg-[var(--bg-primary)]" />
    </div>
  )
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  )
}
